'use client';

import { cn } from '@ahm/common-helpers';
import React, { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  angle: number;
  speed: number;
  size: number;
  opacity: number;
  hue: number;
}

interface AIThinkingAnimationProps {
  className?: string;
  progress?: number;
}

export function AIThinkingAnimation({
  className,
  progress = 0,
}: AIThinkingAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameIdRef = useRef<number | undefined>(undefined);
  const lastFrameTimeRef = useRef<number>(0);
  const isVisibleRef = useRef<boolean>(true);
  const particlesRef = useRef<Particle[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    // Set canvas size
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * window.devicePixelRatio;
      canvas.height = rect.height * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resizeCanvas();

    // Initialize particles
    const numParticles = 20;
    particlesRef.current = Array.from({ length: numParticles }, (_, i) => ({
      x: 0,
      y: 0,
      angle: (i / numParticles) * Math.PI * 2,
      speed: 0.02 + Math.random() * 0.02,
      size: 2 + Math.random() * 3,
      opacity: 0.3 + Math.random() * 0.5,
      hue: 25 + Math.random() * 15, // Orange hue range
    }));

    // Intersection Observer to pause when not visible
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisibleRef.current = entry.isIntersecting;
        });
      },
      { threshold: 0.1 }
    );
    observer.observe(canvas);

    window.addEventListener('resize', resizeCanvas);

    // Animation settings
    const targetFPS = 30;
    const frameInterval = 1000 / targetFPS;

    const animate = (currentTime: number) => {
      animationFrameIdRef.current = requestAnimationFrame(animate);

      // Skip if not visible
      if (!isVisibleRef.current) return;

      // FPS throttling
      const elapsed = currentTime - lastFrameTimeRef.current;
      if (elapsed < frameInterval) return;
      lastFrameTimeRef.current = currentTime - (elapsed % frameInterval);

      const width = canvas.width / window.devicePixelRatio;
      const height = canvas.height / window.devicePixelRatio;
      const centerX = width / 2;
      const centerY = height / 2;
      const baseRadius = Math.min(width, height) * 0.35;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw outer glow
      const gradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        baseRadius * 1.5
      );
      gradient.addColorStop(0, 'rgba(255, 130, 0, 0.1)');
      gradient.addColorStop(0.5, 'rgba(255, 130, 0, 0.05)');
      gradient.addColorStop(1, 'rgba(255, 130, 0, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);

      // Draw orbiting particles
      particlesRef.current.forEach((particle) => {
        particle.angle += particle.speed;

        const orbitRadius =
          baseRadius * (0.8 + Math.sin(particle.angle * 3) * 0.2);
        particle.x = centerX + Math.cos(particle.angle) * orbitRadius;
        particle.y = centerY + Math.sin(particle.angle) * orbitRadius;

        // Particle glow
        const particleGradient = ctx.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          particle.size * 3
        );
        particleGradient.addColorStop(
          0,
          `hsla(${particle.hue}, 100%, 50%, ${particle.opacity})`
        );
        particleGradient.addColorStop(1, 'transparent');

        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = particleGradient;
        ctx.fill();

        // Particle core
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${particle.hue}, 100%, 60%, ${particle.opacity})`;
        ctx.fill();
      });

      // Draw center pulse effect
      const pulseRadius =
        baseRadius * 0.3 * (1 + Math.sin(currentTime / 500) * 0.1);
      const pulseGradient = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        pulseRadius
      );
      pulseGradient.addColorStop(0, 'rgba(255, 130, 0, 0.3)');
      pulseGradient.addColorStop(0.7, 'rgba(255, 130, 0, 0.1)');
      pulseGradient.addColorStop(1, 'transparent');

      ctx.beginPath();
      ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
      ctx.fillStyle = pulseGradient;
      ctx.fill();
    };

    animationFrameIdRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      observer.disconnect();
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className={cn('relative', className)}>
      {/* Canvas for particle animation */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 size-full"
        style={{ willChange: 'transform' }}
      />

      {/* Progress ring overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <svg className="size-28 -rotate-90" viewBox="0 0 100 100">
          {/* Background ring */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth="4"
          />
          {/* Progress ring */}
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="#ff8200"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-500 ease-out"
          />
        </svg>
      </div>

      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-white text-xs font-medium">AI học</span>
        <span className="text-[#ff8200] text-2xl font-bold">+{progress}%</span>
      </div>
    </div>
  );
}
